// src/components/admin/ImportGapItemDialog.tsx
// VIB-781: Dialog returnerer LinkedDraft via callback — parent håndterer batch-import.
// VIB-794: Kommentar-input når valgt prosjekt er internal/hourly.
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useProjects } from "@/hooks/use-projects";
import { useEnsureReisetidLine } from "@/hooks/use-reisetid";
import { useBudgetLinesForProjects } from "@/hooks/use-monday-import";
import { CreateInternalProjectInlineForm } from "@/components/admin/CreateInternalProjectInlineForm";
import type { GapItem } from "@/hooks/use-monday-import-gap";

const PHASE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "pl", label: "Prosjektledelse" },
  { value: "preprod", label: "Preproduksjon" },
  { value: "production", label: "Produksjon" },
  { value: "postprod", label: "Postproduksjon" },
  { value: "stills", label: "Stills" },
  { value: "reise", label: "Reise" },
];

const NONE_BUDGET_LINE = "__none__";
const REISETID_OPTION = "__reisetid__";

export interface LinkedDraft {
  projectId: string;
  phase: string | null;
  budgetLineId: string | null;
  /** VIB-794: Admin-gitt kommentar — brukes i stedet for Monday text-kolonne. */
  comment: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: GapItem | null;
  /** Eksisterende draft hvis itemet allerede er knyttet — prefyller editor. */
  initialDraft?: LinkedDraft;
  /** Kalt når admin trykker "Lagre tilknytning" — parent lagrer draft i state. */
  onConfirm: (itemId: number, draft: LinkedDraft) => void;
}

function fmtHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1).replace(".", ",")}t`;
}

export function ImportGapItemDialog({
  open,
  onOpenChange,
  item,
  initialDraft,
  onConfirm,
}: Props) {
  const { data: projects = [] } = useProjects();
  const { mutateAsync: ensureReisetidLine } = useEnsureReisetidLine();

  const [projectId, setProjectId] = useState<string | null>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const [budgetLineId, setBudgetLineId] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedProject = projects.find((p) => p.id === projectId);
  const isReisetidSelected = budgetLineId === REISETID_OPTION;
  const billsTravelMedgatt = selectedProject?.bills_travel_per_medgatt === true;
  const requiresComment = (!!selectedProject && (
    selectedProject.is_internal === true ||
    selectedProject.project_type === "hourly"
  )) || isReisetidSelected;

  useEffect(() => {
    if (!item) return;
    setShowCreateForm(false);
    if (initialDraft) {
      setProjectId(initialDraft.projectId);
      setPhase(initialDraft.phase);
      setBudgetLineId(initialDraft.budgetLineId);
      setComment(initialDraft.comment ?? "");
      return;
    }
    setBudgetLineId(null);
    setComment("");
    const parsedCode = item.projectCode;
    const matchedProject = parsedCode
      ? projects.find((p) => String(p.project_code) === parsedCode)
      : undefined;
    setProjectId(matchedProject?.id ?? null);
    setPhase(item.parsedPhase);
  }, [item, projects, initialDraft]);

  const { data: budgetLines = [] } = useBudgetLinesForProjects(
    projectId ? [projectId] : [],
  );

  const filteredBudgetLines = useMemo(() => {
    if (!phase) return budgetLines;
    return budgetLines.filter((bl) => bl.phase === phase);
  }, [budgetLines, phase]);

  useEffect(() => {
    if (!item || !projectId || !phase) return;
    if (budgetLineId) return;
    const taskLower = item.parsedTask.toLowerCase().trim();
    const exact = filteredBudgetLines.find(
      (bl) => (bl.description ?? "").toLowerCase().trim() === taskLower,
    );
    if (exact) {
      setBudgetLineId(exact.id);
      return;
    }
    const substr = filteredBudgetLines.find((bl) => {
      const d = (bl.description ?? "").toLowerCase();
      return d && (d.includes(taskLower) || taskLower.includes(d));
    });
    if (substr) setBudgetLineId(substr.id);
  }, [item, projectId, phase, filteredBudgetLines, budgetLineId]);

  if (!item) return null;

  async function handleSave() {
    if (!item || !projectId) return;
    if (requiresComment && !comment.trim()) return;
    let resolvedBudgetLineId: string | null;
    let resolvedPhase = phase;
    if (isReisetidSelected) {
      setSaving(true);
      try {
        resolvedBudgetLineId = await ensureReisetidLine(projectId);
        resolvedPhase = "reise";
      } catch (e) {
        toast.error("Kunne ikke opprette reisetid-linje: " + (e as Error).message);
        setSaving(false);
        return;
      }
      setSaving(false);
    } else {
      resolvedBudgetLineId = budgetLineId === NONE_BUDGET_LINE ? null : budgetLineId;
    }
    onConfirm(item.itemId, {
      projectId,
      phase: resolvedPhase,
      budgetLineId: resolvedBudgetLineId,
      comment: comment.trim() || null,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Knytt til prosjekt</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-1">
            <p className="text-sm font-medium">{item.name}</p>
            <p className="text-xs text-muted-foreground">
              Gruppe: {item.groupTitle} · Fase: {item.parsedPhase ?? "(ikke parsed)"} ·{" "}
              {item.sessionsCount} sessions · {fmtHours(item.totalSeconds)}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium">Prosjekt</label>
              {!showCreateForm && (
                <button
                  type="button"
                  onClick={() => setShowCreateForm(true)}
                  className="text-xs text-primary hover:underline"
                >
                  + Opprett internprosjekt
                </button>
              )}
            </div>
            {showCreateForm ? (
              <CreateInternalProjectInlineForm
                defaultName={item.parsedTask || item.name}
                onCreated={(newId) => {
                  setProjectId(newId);
                  setShowCreateForm(false);
                }}
                onCancel={() => setShowCreateForm(false)}
              />
            ) : (
              <Popover open={projectOpen} onOpenChange={setProjectOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <span className={cn("truncate", !selectedProject && "text-muted-foreground")}>
                      {selectedProject
                        ? selectedProject.project_code
                          ? `${selectedProject.project_code} — ${selectedProject.name}`
                          : selectedProject.name
                        : "Velg prosjekt"}
                    </span>
                    <SrIcon name="chevrons-up-down" className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 w-[var(--radix-popover-trigger-width)]"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Søk i prosjekt..." className="h-9" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>Ingen prosjekt matcher søket.</CommandEmpty>
                      <CommandGroup heading={`${projects.length} prosjekter`}>
                        {projects.map((p) => (
                          <CommandItem
                            key={p.id}
                            value={`${p.project_code ?? ""} ${p.name}`}
                            onSelect={() => {
                              setProjectId(p.id);
                              setBudgetLineId(null);
                              setProjectOpen(false);
                            }}
                          >
                            <span className="text-xs truncate">
                              {p.project_code ? `${p.project_code} — ${p.name}` : p.name}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium">Fase</label>
            <Select value={phase ?? ""} onValueChange={(v) => setPhase(v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Velg fase" />
              </SelectTrigger>
              <SelectContent>
                {PHASE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium">Budget-linje</label>
            <Select
              value={budgetLineId ?? NONE_BUDGET_LINE}
              onValueChange={(v) => setBudgetLineId(v)}
              disabled={!projectId}
            >
              <SelectTrigger className={cn("h-9", !projectId && "opacity-50")}>
                <SelectValue placeholder="Ingen (project_only)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_BUDGET_LINE}>
                  (ingen — project_only)
                </SelectItem>
                {billsTravelMedgatt && (
                  <SelectItem value={REISETID_OPTION}>
                    Reisetid (medgått)
                  </SelectItem>
                )}
                {filteredBudgetLines.map((bl) => (
                  <SelectItem key={bl.id} value={bl.id}>
                    {bl.description ?? "(uten beskrivelse)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {requiresComment && (
            <div className="space-y-2">
              <label className="text-xs font-medium">
                Kommentar <span className="text-red-600">*</span>
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Hva ble gjort?"
                className="min-h-[60px]"
              />
              <p className="text-[11px] text-muted-foreground">
                Påkrevd for {selectedProject?.is_internal ? "internprosjekt" : "T&M-prosjekt"}.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            onClick={handleSave}
            disabled={!projectId || (requiresComment && !comment.trim()) || saving}
          >
            {saving ? "Lagrer…" : "Lagre tilknytning"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
